import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft, GripVertical, Plus, X, Loader2, Volume2,
  Clock, Globe, Zap, RefreshCw, Send, Check, Pencil, Timer, Mic2, Megaphone,
  ListChecks, UserCircle2, Plane, Filter,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { beltAssignmentsAPI, airportAnnouncementsAPI } from '../../services/api';
import { useSupervisorSocket } from '../../hooks/useGateSocket';
import StatusBadge from '../../components/UI/StatusBadge';

// ── Status config ─────────────────────────────────────────────────────────────
// Same tones as every other page showing this AirportAnnouncement status set.
// 'broadcasting' was solid-emerald here (blue elsewhere) and 'cancelled' was
// red here (neutral elsewhere, matching 'rejected' instead of being its own
// thing) — both now unified.

const STATUS = {
  pending:      { label: 'Pending',      tone: 'neutral' },
  generating:   { label: 'Generating',   tone: 'info' },
  ready:        { label: 'Ready',        tone: 'success' },
  broadcasting: { label: 'Broadcasting', tone: 'info', pulse: true },
  played:       { label: 'Played',       tone: 'neutral' },
  rejected:     { label: 'Rejected',     tone: 'danger' },
  cancelled:    { label: 'Cancelled',    tone: 'neutral' },
};

// ── Announcement type config (derived from the title convention set by
// flightSyncService.js / the ad-hoc endpoints — same convention BeltAssignmentDetailPage
// already uses for its timeline) ────────────────────────────────────────────────

const TYPE_CONFIG = {
  belt_change:       { label: 'Belt Change',              badge: 'bg-indigo-100 text-indigo-700',  border: 'border-l-indigo-400' },
  first_bag:         { label: 'First Bag',                badge: 'bg-teal-100 text-teal-700',      border: 'border-l-teal-400' },
  disembark_delay:   { label: 'Disembarkation Delay',     badge: 'bg-red-100 text-red-700',        border: 'border-l-red-400' },
  disembark_resumed: { label: 'Disembarkation Resumed',   badge: 'bg-emerald-100 text-emerald-700', border: 'border-l-emerald-400' },
  weather_alert:     { label: 'Weather Alert',            badge: 'bg-yellow-100 text-yellow-800',  border: 'border-l-yellow-400' },
  passenger_paging:  { label: 'Passenger Paging',         badge: 'bg-pink-100 text-pink-700',       border: 'border-l-pink-400' },
  adhoc:             { label: 'Ad-hoc',                   badge: 'bg-purple-100 text-purple-700',  border: 'border-l-purple-400' },
  zone_announcement: { label: 'Zone Announcement',        badge: 'bg-blue-100 text-blue-700',      border: 'border-l-blue-400' },
};

function typeKeyFromTitle(title = '') {
  if (title.startsWith('Belt announcement')) return 'belt_change';
  if (title.startsWith('First bag')) return 'first_bag';
  if (title.startsWith('Disembarkation delay')) return 'disembark_delay';
  if (title.startsWith('Disembarkation resumed')) return 'disembark_resumed';
  if (title.startsWith('Ad-hoc')) return 'adhoc';
  if (/weather|lightning/i.test(title)) return 'weather_alert';
  return null;
}

function typeConf(item) {
  const key = typeKeyFromTitle(item.title || '') || (item.announcement_type === 'passenger_paging' ? 'passenger_paging' : 'zone_announcement');
  return TYPE_CONFIG[key] || { label: item.title || '—', badge: 'bg-slate-100 text-slate-600', border: 'border-l-slate-300' };
}

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return 'ASAP';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function toTimeInput(iso) {
  const d = iso ? new Date(iso) : new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function timeInputToDate(timeStr, referenceIso) {
  const ref = referenceIso ? new Date(referenceIso) : new Date();
  const [h, m] = timeStr.split(':').map(Number);
  const d = new Date(ref);
  d.setHours(h, m, 0, 0);
  return d;
}

function timeUntilScheduled(iso) {
  if (!iso) return { label: 'ASAP', urgent: true };
  const diff = new Date(iso) - Date.now();
  if (diff <= 0) return { label: 'overdue', urgent: true };
  const m = Math.floor(diff / 60000);
  if (m < 2) return { label: 'imminent', urgent: true };
  return { label: `in ${m}m`, urgent: m < 10 };
}

// ── Inline time editor ────────────────────────────────────────────────────────

function TimeEditor({ ann, terminal, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(toTimeInput(ann.scheduled_at));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setValue(toTimeInput(ann.scheduled_at)); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open, ann.scheduled_at]);

  async function save() {
    const newDate = timeInputToDate(value, ann.scheduled_at);
    if (isNaN(newDate.getTime())) { toast.error('Invalid time'); return; }
    setSaving(true);
    try {
      await beltAssignmentsAPI.rescheduleZoneItem(terminal, ann.id, newDate.toISOString());
      toast.success('Time updated');
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update time'); }
    finally { setSaving(false); }
  }

  function handleKey(e) {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') setOpen(false);
  }

  const { label: countdownLabel, urgent } = timeUntilScheduled(ann.scheduled_at);

  if (open) {
    return (
      <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
        <input ref={inputRef} type="time" value={value}
          onChange={e => setValue(e.target.value)} onKeyDown={handleKey}
          className="border border-blue-400 rounded-lg px-2 py-1 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-400 w-[8rem] min-w-[8rem] bg-white"
        />
        <button onClick={save} disabled={saving}
          className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
        </button>
        <button onClick={() => setOpen(false)}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X size={11} />
        </button>
      </div>
    );
  }

  return (
    <button onClick={() => setOpen(true)}
      className="flex items-center gap-1.5 ml-auto flex-shrink-0 group/timebtn rounded-lg px-2 py-1 hover:bg-slate-50 transition-colors"
      title="Click to adjust time"
    >
      <div className="text-right">
        <div className="flex items-center gap-1 justify-end">
          <Clock size={10} className="text-slate-400" />
          <span className="font-mono text-xs text-slate-600 font-medium">{fmtTime(ann.scheduled_at)}</span>
          <Pencil size={9} className="opacity-0 group-hover/timebtn:opacity-100 text-blue-500 transition-opacity" />
        </div>
        <span className={`text-[10px] font-medium ${urgent ? 'text-orange-500' : 'text-slate-400'}`}>{countdownLabel}</span>
      </div>
    </button>
  );
}

// ── Shift-all bar ─────────────────────────────────────────────────────────────

function ShiftBar({ terminal, flightNumber, hasItems, onShifted }) {
  const [shifting, setShifting] = useState(false);

  async function shift(delta) {
    setShifting(true);
    try {
      await beltAssignmentsAPI.shiftZoneQueue(terminal, delta, flightNumber || undefined);
      toast.success(`${flightNumber ? `${flightNumber}'s` : 'All'} remaining announcements shifted ${delta > 0 ? '+' : ''}${delta}m`);
      onShifted();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to shift schedule'); }
    finally { setShifting(false); }
  }

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-2xl mb-4 transition-opacity ${!hasItems ? 'opacity-40 pointer-events-none' : ''}`}>
      <div className="flex items-center gap-1.5 flex-shrink-0 w-full sm:w-auto">
        {shifting ? <Loader2 size={13} className="animate-spin text-amber-500" /> : <Timer size={13} className="text-amber-600" />}
        <span className="text-xs font-bold text-amber-700">Shift all</span>
      </div>

      <div className="hidden sm:block h-4 w-px bg-amber-200" />

      <div className="flex items-center gap-1">
        <span className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide mr-0.5">Earlier</span>
        {[-15, -10, -5].map(d => (
          <button key={d} onClick={() => shift(d)} disabled={shifting || !hasItems}
            className="px-2.5 py-1 rounded-xl text-xs font-bold border border-blue-200 text-blue-700 bg-white hover:bg-blue-50 transition-colors disabled:opacity-50 shadow-sm">
            {d}m
          </button>
        ))}
      </div>

      <div className="hidden sm:block h-4 w-px bg-amber-200" />

      <div className="flex items-center gap-1">
        <span className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide mr-0.5">Later</span>
        {[5, 10, 15].map(d => (
          <button key={d} onClick={() => shift(d)} disabled={shifting || !hasItems}
            className="px-2.5 py-1 rounded-xl text-xs font-bold border border-orange-200 text-orange-700 bg-white hover:bg-orange-50 transition-colors disabled:opacity-50 shadow-sm">
            +{d}m
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Sortable announcement card ────────────────────────────────────────────────

function SortableAnnCard({ ann, terminal, onCancel, onSaved, insertForm, onInsertAfter }) {
  const draggable = ['pending', 'generating', 'ready'].includes(ann.status);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ann.id, disabled: !draggable,
  });

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };
  const sc = STATUS[ann.status] || STATUS.pending;
  const tc = typeConf(ann);

  return (
    <div ref={setNodeRef} style={style} className="group/card">
      <div className={`flex items-stretch bg-white border border-slate-200 rounded-2xl overflow-hidden transition-shadow
        ${isDragging ? 'shadow-2xl ring-2 ring-blue-400' : 'shadow-sm hover:shadow-md'}
        ${ann.status === 'cancelled' ? 'opacity-40' : ''}`}
      >
        <div className={`w-1 flex-shrink-0 ${tc.border} border-l-4 rounded-l-2xl`} />

        <div
          {...(draggable ? { ...attributes, ...listeners } : {})}
          className={`flex items-center px-2 ${draggable ? 'touch-none cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400' : 'text-slate-100 cursor-default'}`}
        >
          <GripVertical size={16} />
        </div>

        <div className="flex-1 min-w-0 px-3 py-3">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg ${tc.badge}`}>
              {tc.label === 'Ad-hoc' && <Zap size={10} />}
              {tc.label}
            </span>
            <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} dot={ann.status === 'broadcasting'} />
            {ann.status === 'generating' && <Loader2 size={10} className="animate-spin text-blue-500" />}

            {draggable ? (
              <TimeEditor ann={ann} terminal={terminal} onSaved={onSaved} />
            ) : (
              <span className="text-xs text-slate-400 flex items-center gap-1 ml-auto">
                <Clock size={10} /> {fmtTime(ann.played_at || ann.scheduled_at)}
              </span>
            )}
          </div>

          {ann.english_text && (
            <p className="text-sm text-slate-700 leading-snug line-clamp-2 font-medium">{ann.english_text}</p>
          )}

          {ann.secondary_text && (
            <p className="text-xs text-slate-400 mt-1.5 line-clamp-1 flex items-center gap-1.5">
              <Globe size={10} className="flex-shrink-0 text-slate-300" />
              <span className="italic">{ann.secondary_text}</span>
            </p>
          )}
          {!ann.secondary_text && ann.english_only && (
            <p className="text-xs text-slate-300 mt-1.5 flex items-center gap-1.5">
              English only
            </p>
          )}
        </div>

        {draggable && (
          <div className="flex items-center pr-3">
            <button onClick={() => onCancel(ann.id)}
              className="p-1.5 rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover/card:opacity-100 [@media(hover:none)]:opacity-100"
              title="Remove from queue"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      {draggable && <InsertSlot annId={ann.id} form={insertForm} onOpen={onInsertAfter} />}
    </div>
  );
}

function DragCard({ ann }) {
  const tc = typeConf(ann || {});
  const sc = STATUS[ann?.status] || STATUS.pending;
  return (
    <div className="flex items-stretch bg-white border-2 border-blue-400 rounded-2xl overflow-hidden shadow-2xl w-80">
      <div className={`w-1 flex-shrink-0 ${tc.border} border-l-4`} />
      <div className="px-3 py-3 flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-lg ${tc.badge}`}>{tc.label}</span>
          <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} size="sm" />
          <span className="text-xs text-slate-400 ml-auto font-mono">{fmtTime(ann?.scheduled_at)}</span>
        </div>
        {ann?.english_text && <p className="text-sm text-slate-700 line-clamp-2 font-medium">{ann.english_text}</p>}
      </div>
    </div>
  );
}

// ── Insert slot / form ────────────────────────────────────────────────────────

function InsertSlot({ annId, form, onOpen }) {
  const isOpen = form?.insertAfterId === annId;
  if (isOpen) return null;
  return (
    <div className="relative my-1 flex items-center justify-center group/slot h-6">
      <div className="absolute inset-x-0 top-1/2 h-px bg-slate-100 group-hover/slot:bg-blue-100 transition-colors" />
      <button onClick={() => onOpen(annId)}
        className="relative opacity-0 group-hover/slot:opacity-100 flex items-center gap-1 px-3 py-0.5 rounded-full bg-white border border-blue-200 text-blue-600 text-xs font-semibold hover:bg-blue-50 shadow-sm transition-all">
        <Plus size={11} /> Insert here
      </button>
    </div>
  );
}

function TopInsertSlot({ form, onOpen }) {
  const isOpen = form?.insertAfterId === null && form !== null;
  if (isOpen) return null;
  return (
    <div className="flex items-center justify-center mb-2 group/slot h-6">
      <button onClick={() => onOpen(null)}
        className="opacity-0 group-hover/slot:opacity-100 flex items-center gap-1 px-3 py-0.5 rounded-full bg-white border border-blue-200 text-blue-600 text-xs font-semibold hover:bg-blue-50 shadow-sm transition-all">
        <Plus size={11} /> Insert at top
      </button>
    </div>
  );
}

function InsertForm({ insertAfterId, onSubmit, onClose, loading }) {
  const [text, setText] = useState('');
  const textRef = useRef(null);
  useEffect(() => { textRef.current?.focus(); }, []);

  function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim()) return;
    onSubmit(text.trim(), insertAfterId);
  }

  return (
    <div className="my-2 bg-purple-50 border border-purple-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-purple-700 mb-3 flex items-center gap-1.5 uppercase tracking-wide">
        <Zap size={12} /> Insert ad-hoc announcement{insertAfterId === null ? ' (at top)' : ''}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <textarea ref={textRef} value={text} onChange={e => setText(e.target.value)}
          placeholder="Type the announcement text in English…"
          rows={3} maxLength={1000}
          className="w-full border border-purple-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none bg-white shadow-sm"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-400">{text.length}/1000 · Translation & audio auto-generated</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={loading}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading || !text.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50 transition-colors shadow-sm">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {loading ? 'Adding…' : 'Add to queue'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function BeltZoneQueuePage() {
  const { terminal } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.pathname.startsWith('/admin') ? '/admin/belt-assignments' : '/supervisor/belts';

  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [activeAnn, setActiveAnn] = useState(null);
  const [insertForm, setInsertForm] = useState(null);
  const [inserting, setInserting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const flightFilter = searchParams.get('flight') || '';

  function setFlightFilter(flight) {
    setSearchParams(flight ? { flight } : {}, { replace: true });
  }

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await beltAssignmentsAPI.getZoneQueue(terminal, flightFilter);
      const data = res.data.data;
      setQueueData(data);
      setUpcoming(data.upcoming || []);
    } catch {
      toast.error('Failed to load zone queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [terminal, flightFilter]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);

  useSupervisorSocket({
    onEvent: (evt) => {
      if (['announcement:status_change', 'announcement:submitted'].includes(evt)) load(true);
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragStart(event) {
    setActiveAnn(upcoming.find(a => a.id === event.active.id) || null);
  }

  async function handleDragEnd(event) {
    setActiveAnn(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = upcoming.findIndex(a => a.id === active.id);
    const newIdx = upcoming.findIndex(a => a.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(upcoming, oldIdx, newIdx);
    setUpcoming(reordered);
    try {
      await beltAssignmentsAPI.reorderZoneQueue(terminal, reordered.map(a => a.id));
      toast.success('Queue reordered');
      load(true);
    } catch {
      setUpcoming(upcoming);
      toast.error('Failed to save order');
    }
  }

  async function handleInsert(text, insertAfterId) {
    setInserting(true);
    try {
      await beltAssignmentsAPI.sendZoneAdhoc(terminal, {
        text,
        insert_after_id: insertAfterId,
        flight_number: flightFilter || undefined,
      });
      toast.success('Ad-hoc announcement added');
      setInsertForm(null);
      load(true);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to insert'); }
    finally { setInserting(false); }
  }

  async function handleCancel(annId) {
    if (!confirm('Remove this announcement from the queue?')) return;
    try {
      await airportAnnouncementsAPI.cancel(annId);
      setUpcoming(u => u.filter(a => a.id !== annId));
      toast.success('Announcement removed');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to remove'); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-400">Loading zone queue…</span>
        </div>
      </div>
    );
  }

  if (!queueData) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500 font-semibold">Zone not found</p>
      </div>
    );
  }

  const { zone, operators, flights, nowPlaying, history } = queueData;
  const playing = nowPlaying || [];

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      {/* Header (phones: back + refresh on one row, zone card full width below) */}
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 mb-5">
        <button onClick={() => navigate(backPath)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors flex-shrink-0">
          <ArrowLeft size={18} />
        </button>

        <div className="order-last sm:order-none w-full sm:w-auto sm:flex-1 min-w-0 bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl px-4 py-3 shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <ListChecks size={16} className="text-white/70" />
                <h1 className="text-lg font-black text-white tracking-tight">{zone.zone_name}</h1>
                <span className="text-xs font-bold px-2 py-0.5 rounded-lg bg-white/20 text-white/90 backdrop-blur">
                  {zone.zone_code}
                </span>
              </div>
              <div className="flex items-start gap-1.5 text-white/70 text-xs">
                <UserCircle2 size={12} className="flex-shrink-0 mt-px" />
                {operators.length === 0 ? 'No operators assigned' : operators.map(o => o.name).join(', ')}
              </div>
            </div>
          </div>
        </div>

        <button onClick={() => load(true)} disabled={refreshing} className="ml-auto sm:ml-0 p-2 rounded-xl hover:bg-slate-100 text-slate-400 flex-shrink-0 transition-colors" title="Refresh">
          <RefreshCw size={16} className={refreshing ? 'animate-spin text-blue-500' : ''} />
        </button>
      </div>

      {/* Flight filter — a terminal's PA is shared by every flight currently on the
          board, so the queue below is terminal-wide by default; this narrows it to one
          flight's own announcements (what "Manage Queue" opens to when reached from a
          specific Belt Assignments row). */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter size={13} className="text-slate-400 flex-shrink-0" />
        <select
          value={flightFilter}
          onChange={e => setFlightFilter(e.target.value)}
          className="text-xs font-semibold border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="">All flights in {zone.zone_code}</option>
          {(flights || []).map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        {flightFilter && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
            <Plane size={11} />
            Showing {flightFilter} only
            <button onClick={() => setFlightFilter('')} className="text-blue-400 hover:text-blue-700 ml-0.5" title="Clear filter">
              <X size={12} />
            </button>
          </span>
        )}
      </div>

      {/* Now Playing */}
      {playing.length > 0 && (
        <div className="bg-emerald-600 rounded-2xl px-4 py-4 mb-4 shadow-lg shadow-emerald-100">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex gap-0.5 items-end h-4">
              {[3, 5, 4, 6, 3].map((h, i) => (
                <div key={i} className="w-1 bg-white/70 rounded-full animate-pulse" style={{ height: `${h * 3}px`, animationDelay: `${i * 100}ms` }} />
              ))}
            </div>
            <span className="text-sm font-black text-white uppercase tracking-wide">Now Playing</span>
          </div>
          {playing.map(ann => {
            const tc = typeConf(ann);
            return (
              <div key={ann.id}>
                <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-lg mb-1.5 bg-white/20 text-white">
                  {tc.label}
                </span>
                {ann.english_text && <p className="text-sm text-white font-medium leading-snug">{ann.english_text}</p>}
                {ann.secondary_text && (
                  <p className="text-xs text-emerald-200 mt-1.5 flex items-center gap-1.5 italic">
                    <Globe size={10} /> {ann.secondary_text}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upcoming queue */}
      <div className="mb-2">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-black text-slate-700 uppercase tracking-wide whitespace-nowrap">Upcoming Queue</h2>
            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
              {upcoming.length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setInsertForm({ insertAfterId: null })}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 transition-colors shadow-sm whitespace-nowrap">
              <Plus size={12} /> Ad-hoc
            </button>
            <span className="hidden sm:flex text-xs text-slate-400 items-center gap-1">
              <GripVertical size={12} /> Drag to reorder
            </span>
          </div>
        </div>

        <ShiftBar terminal={terminal} flightNumber={flightFilter} hasItems={upcoming.length > 0} onShifted={() => load(true)} />

        {upcoming.length === 0 && !insertForm ? (
          <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-slate-200">
            <Mic2 size={36} className="text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500 font-semibold">No upcoming announcements</p>
            <p className="text-slate-400 text-sm mt-1 mb-4">
              {flightFilter ? `Nothing queued for ${flightFilter} right now.` : 'Nothing queued for this zone right now.'}
            </p>
            {/* Purple to match this zone-queue's own ad-hoc-insert theme (InsertForm,
                the "+ Ad-hoc" header button) — not the app's general primary action
                color, so left as its own custom button rather than Button's primary. */}
            <button onClick={() => setInsertForm({ insertAfterId: null })}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition-colors shadow-sm">
              <Plus size={14} /> Add ad-hoc announcement
            </button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            {insertForm?.insertAfterId === null ? (
              <InsertForm insertAfterId={null} onSubmit={handleInsert} onClose={() => setInsertForm(null)} loading={inserting} />
            ) : (
              <TopInsertSlot form={insertForm} onOpen={(id) => setInsertForm({ insertAfterId: id })} />
            )}

            <SortableContext items={upcoming.map(a => a.id)} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-2">
                {upcoming.map(ann => (
                  <React.Fragment key={ann.id}>
                    <SortableAnnCard
                      ann={ann}
                      terminal={terminal}
                      onCancel={handleCancel}
                      onSaved={() => load(true)}
                      insertForm={insertForm}
                      onInsertAfter={(id) => setInsertForm({ insertAfterId: id })}
                    />
                    {insertForm?.insertAfterId === ann.id && (
                      <InsertForm insertAfterId={ann.id} onSubmit={handleInsert} onClose={() => setInsertForm(null)} loading={inserting} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </SortableContext>

            <DragOverlay>{activeAnn ? <DragCard ann={activeAnn} /> : null}</DragOverlay>
          </DndContext>
        )}
      </div>

      {/* History */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-black text-slate-700 uppercase tracking-wide">History</h2>
          <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
            {history.length}
          </span>
        </div>
        {history.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-slate-200">
            <Megaphone size={30} className="text-slate-200 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">No past announcements for this zone yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {history.map(item => {
              const tc = typeConf(item);
              const sc = STATUS[item.status] || STATUS.played;
              return (
                <div key={item.id} className="flex items-stretch bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm opacity-80">
                  <div className={`w-1 flex-shrink-0 ${tc.border} border-l-4`} />
                  <div className="flex-1 min-w-0 px-3 py-3">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-lg ${tc.badge}`}>{tc.label}</span>
                      <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} />
                      <span className="text-xs text-slate-400 ml-auto flex items-center gap-1">
                        <Clock size={10} /> {fmtTime(item.played_at || item.scheduled_at)}
                      </span>
                    </div>
                    {item.english_text && <p className="text-sm text-slate-700 leading-snug line-clamp-2 font-medium">{item.english_text}</p>}
                    {item.secondary_text && (
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                        <Globe size={10} className="flex-shrink-0 text-slate-300" />
                        <span className="italic">{item.secondary_text}</span>
                      </p>
                    )}
                    {item.audio_file_path && (
                      <a
                        href={`/uploads/${item.audio_file_path.split('/').pop()}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline mt-1.5"
                      >
                        <Volume2 size={12} /> Preview audio
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
